import { Check, Copy, Download, Megaphone, Pencil, Plus, Trash2, Upload, X } from "lucide-react"
import { Fragment } from "preact"
import { createPortal } from "preact/compat"
import { useEffect, useMemo, useRef, useState } from "preact/hooks"
import {
  ColumnDef,
  flexRender,
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  PaginationState,
  SortingState,
  useReactTable,
} from "@tanstack/react-table"
import { format, parseISO } from "date-fns"
import {
  FileImport,
  FileImportRow,
  Split,
  DeductionSearchResult,
  TransactionKind,
  getFileImport,
  getFileImportRows,
  getFileImportExportUrl,
  getFileImportDownloadUrl,
  updateFileImportRow,
  searchDeductions,
  importDeductions,
  type ImportDeductionsResponse,
} from "@/api/file_import"
import { FileImportHelp, label_for_file_import_format } from "@/file_imports/file_import_help"
import { ImportDeductionResultsDialog } from "@/file_imports/import_deduction_results_dialog"
import { capitalCase } from "change-case"
import { useAsyncEffect } from "@/utils"
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table"
import { Button } from "@/components/ui/button"
import { toast } from "@/components/ui/use-toast"
import { Checkbox } from "@/components/ui/checkbox"
import { ColumnHeader } from "@/deduction_table/column_header"
import { LedgerAccounts } from "@/accounting/accounts"
import { SelectItem } from "@/accounting/autocomplete_select"
import { CenterSpinner } from "@/app/loading"
import {
  FileImportFilterState,
  FilterState,
  KindFilter,
  MatchingFilter,
  CodingFilter,
  createEmptyFilter,
  filter_for_import_result_row_ids,
  isFilterEmpty,
} from "@/file_imports/filter_state"
import { RefreshFlag, triggerRefreshFlag } from "@/refresh_flag"

interface FileImportDetailProps {
  params: { id: string }
}

/** Left border colors for BC SoT import merge groups (cycles by group index). */
const BC_SOT_GROUP_BORDER_PALETTE = [
  "border-l-[3px] border-l-sky-500",
  "border-l-[3px] border-l-violet-500",
  "border-l-[3px] border-l-amber-500",
  "border-l-[3px] border-l-emerald-500",
  "border-l-[3px] border-l-rose-500",
  "border-l-[3px] border-l-cyan-600",
] as const

function bc_sot_import_row_left_border_class(row: FileImportRow): string {
  if (row.deduction_id) {
    return ""
  }
  const g = row.bc_sot_import_group
  if (g == null || g < 1) {
    return ""
  }
  return BC_SOT_GROUP_BORDER_PALETTE[(g - 1) % BC_SOT_GROUP_BORDER_PALETTE.length]
}

interface CopyableCellProps {
  value: string | undefined
  wide?: boolean
}

function CopyableCell({ value, wide }: CopyableCellProps) {
  const [showTooltip, setShowTooltip] = useState(false)
  const [copied, setCopied] = useState(false)
  const [tooltipPos, setTooltipPos] = useState({ top: 0, left: 0 })
  const cellRef = useRef<HTMLDivElement>(null)

  if (!value) return null

  const trimmedValue = value.trim()

  async function handleCopy() {
    await navigator.clipboard.writeText(trimmedValue)
    setCopied(true)
    setTimeout(() => setCopied(false), 1500)
  }

  function handleMouseEnter() {
    if (cellRef.current && value && value.length > 30) {
      const rect = cellRef.current.getBoundingClientRect()
      setTooltipPos({ top: rect.top - 4, left: rect.left })
    }
    setShowTooltip(true)
  }

  const tooltipElement = showTooltip && value.length > 30 && createPortal(
    <div
      class="fixed z-[9999] p-2 bg-gray-900 text-white text-xs rounded shadow-lg max-w-sm whitespace-pre-wrap break-words"
      style={{ top: tooltipPos.top, left: tooltipPos.left, transform: "translateY(-100%)" }}
    >
      {value}
    </div>,
    document.body
  )

  return (
    <div
      ref={cellRef}
      class="relative group"
      onMouseEnter={handleMouseEnter}
      onMouseLeave={() => setShowTooltip(false)}>
      <div class="flex items-center gap-1">
        <span class={`text-sm block truncate ${wide ? "w-[30rem]" : "max-w-56"}`}>{value}</span>
        <button
          onClick={handleCopy}
          class="opacity-0 group-hover:opacity-100 text-gray-400 hover:text-gray-600 transition-opacity">
          {copied ? <Check size={14} className="text-green-500" /> : <Copy size={14} />}
        </button>
      </div>
      {tooltipElement}
    </div>
  )
}

interface EditableCellProps {
  value: string | undefined
  rowId: string
  field: string
  onSave: (rowId: string, field: string, value: string) => Promise<void>
}

// Lightweight wrapper - only mounts the actual editor when clicked
function EditableCell({ value, rowId, field, onSave }: EditableCellProps) {
  const [editing, setEditing] = useState(false)
  const [localValue, setLocalValue] = useState(value ?? "")
  const lastPropValue = useRef(value)

  // Only sync from prop if it actually changed externally
  if (value !== lastPropValue.current) {
    lastPropValue.current = value
    setLocalValue(value ?? "")
  }

  if (editing) {
    return (
      <EditableCellEditor
        value={localValue}
        rowId={rowId}
        field={field}
        onSave={async (id, f, newValue) => {
          setLocalValue(newValue)
          await onSave(id, f, newValue)
        }}
        onClose={() => setEditing(false)}
      />
    )
  }

  return (
    <div
      class="w-full min-w-20 h-6 cursor-text"
      onMouseDown={e => {
        e.preventDefault()
        e.stopPropagation()
        setEditing(true)
      }}>
      <span class="text-sm px-1 block h-6 leading-6 truncate">{localValue}</span>
    </div>
  )
}

// The actual editor - only mounted when editing
function EditableCellEditor({ value, rowId, field, onSave, onClose }: EditableCellProps & { onClose: () => void }) {
  const [localValue, setLocalValue] = useState(value ?? "")
  const inputRef = useRef<HTMLInputElement>(null)

  useEffect(() => {
    if (inputRef.current) {
      inputRef.current.focus()
      inputRef.current.select()
    }
  }, [])

  function handleSave() {
    onClose()
    if (localValue !== (value ?? "")) {
      onSave(rowId, field, localValue)
    }
  }

  function handleKeyDown(e: KeyboardEvent) {
    if (e.key === "Enter") {
      ;(e.target as HTMLInputElement).blur()
    } else if (e.key === "Escape") {
      onClose()
    }
  }

  return (
    <div class="w-full min-w-[80px] h-6">
      <input
        ref={inputRef}
        type="text"
        value={localValue}
        onInput={e => setLocalValue((e.target as HTMLInputElement).value)}
        onBlur={handleSave}
        onKeyDown={handleKeyDown}
        class="text-sm w-full h-7 px-2 bg-blue-50 rounded outline-none border border-blue-200 leading-7 box-border"
      />
    </div>
  )
}

const KIND_OPTIONS: { value: TransactionKind | "none"; label: string }[] = [
  { value: "none", label: "None" },
  { value: "invoice", label: "Invoice" },
  { value: "prepayment", label: "Prepayment" },
  { value: "repayment", label: "Repayment" },
  { value: "deduction", label: "Deduction" },
  { value: "correction", label: "Correction" },
]

interface EditableKindCellProps {
  value: TransactionKind | undefined
  rowId: string
  onSave: (rowId: string, value: TransactionKind | null) => void
}

// Lightweight wrapper - only mounts select when clicked
function EditableKindCell({ value, rowId, onSave }: EditableKindCellProps) {
  const [editing, setEditing] = useState(false)
  const [localValue, setLocalValue] = useState(value)
  const lastPropValue = useRef(value)

  // Only sync from prop if it actually changed externally
  if (value !== lastPropValue.current) {
    lastPropValue.current = value
    setLocalValue(value)
  }

  if (editing) {
    return (
      <EditableKindSelect
        value={localValue}
        onChange={newValue => {
          setLocalValue(newValue ?? undefined)
          setEditing(false)
          onSave(rowId, newValue)
        }}
        onClose={() => setEditing(false)}
      />
    )
  }

  const displayLabel = localValue ? KIND_OPTIONS.find(o => o.value === localValue)?.label : "None"

  return (
    <div
      class="w-full min-w-[80px] h-6 cursor-pointer"
      onMouseDown={e => {
        e.preventDefault()
        e.stopPropagation()
        setEditing(true)
      }}>
      <span class={`text-sm px-1 block h-6 leading-6 truncate ${!localValue ? "text-gray-400" : ""}`}>
        {displayLabel}
      </span>
    </div>
  )
}

// The actual select - only mounted when editing
function EditableKindSelect({
  value,
  onChange,
  onClose,
}: {
  value: TransactionKind | undefined
  onChange: (value: TransactionKind | null) => void
  onClose: () => void
}) {
  const selectRef = useRef<HTMLSelectElement>(null)

  useEffect(() => {
    selectRef.current?.focus()
  }, [])

  function handleChange(newValue: string) {
    onChange(newValue === "none" ? null : (newValue as TransactionKind))
  }

  return (
    <select
      ref={selectRef}
      value={value ?? "none"}
      onChange={e => handleChange(e.currentTarget.value)}
      onBlur={onClose}
      class="text-sm h-6 px-0 py-0 m-0 bg-blue-50 rounded outline-none border border-blue-200 leading-6 box-border max-w-[90px]">
      {KIND_OPTIONS.map(opt => (
        <option key={opt.value} value={opt.value}>
          {opt.label}
        </option>
      ))}
    </select>
  )
}

interface MatchAutocompleteProps {
  deductionId: string | undefined
  matchedInvoiceNumber: string | undefined
  rowId: string
  onSelect: (rowId: string, deductionId: string) => void
}

function kindName(ded: DeductionSearchResult): string {
  return capitalCase(ded.kind)
}

// Lightweight wrapper - only mounts the search UI when clicked
function MatchAutocomplete({ deductionId, matchedInvoiceNumber, rowId, onSelect }: MatchAutocompleteProps) {
  const [editing, setEditing] = useState(false)
  const [localDeductionId, setLocalDeductionId] = useState(deductionId)
  const [localInvoiceNumber, setLocalInvoiceNumber] = useState(matchedInvoiceNumber)
  const lastPropDeductionId = useRef(deductionId)
  const lastPropInvoiceNumber = useRef(matchedInvoiceNumber)

  // Only sync from prop if it actually changed externally
  if (matchedInvoiceNumber !== lastPropInvoiceNumber.current) {
    lastPropInvoiceNumber.current = matchedInvoiceNumber
    setLocalInvoiceNumber(matchedInvoiceNumber)
  }
  if (deductionId !== lastPropDeductionId.current) {
    lastPropDeductionId.current = deductionId
    setLocalDeductionId(deductionId)
  }

  if (editing) {
    return (
      <DeductionSearchDropdown
        onSelect={(id, invoiceNumber) => {
          setLocalDeductionId(id)
          setLocalInvoiceNumber(invoiceNumber)
          setEditing(false)
          onSelect(rowId, id)
        }}
        onClose={() => setEditing(false)}
      />
    )
  }

  const hasValue = localDeductionId && localInvoiceNumber

  // If empty, clicking the cell goes into editing mode
  if (!hasValue) {
    return (
      <div
        class="h-7 cursor-text flex items-center min-w-[100px]"
        onMouseDown={e => {
          e.preventDefault()
          e.stopPropagation()
          setEditing(true)
        }}
      />
    )
  }

  // If has value, show link + pencil icon
  return (
    <div class="h-7 flex items-center gap-2 min-w-[100px] whitespace-nowrap">
      <a href={`/deduction/${localDeductionId}`} class="text-sm text-blue-600 hover:text-blue-800">
        {localInvoiceNumber}
      </a>
      <button
        class="text-gray-400 hover:text-gray-600"
        onMouseDown={e => {
          e.preventDefault()
          e.stopPropagation()
          setEditing(true)
        }}>
        <Pencil size={16} />
      </button>
    </div>
  )
}

// The actual search dropdown - only mounted when editing
function DeductionSearchDropdown({
  onSelect,
  onClose,
}: {
  onSelect: (deductionId: string, invoiceNumber: string) => void
  onClose: () => void
}) {
  const [query, setQuery] = useState("")
  const [results, setResults] = useState<DeductionSearchResult[]>([])
  const [selectedIndex, setSelectedIndex] = useState(0)
  const inputRef = useRef<HTMLInputElement>(null)
  const containerRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    inputRef.current?.focus()
  }, [])

  useAsyncEffect(async () => {
    if (query.length < 2) {
      setResults([])
      return
    }
    const data = await searchDeductions(query)
    setResults(data)
    setSelectedIndex(0)
  }, [query])

  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        onClose()
      }
    }
    document.addEventListener("mousedown", handleClickOutside)
    return () => document.removeEventListener("mousedown", handleClickOutside)
  }, [onClose])

  function handleKeyDown(e: KeyboardEvent) {
    if (e.key === "ArrowDown") {
      e.preventDefault()
      setSelectedIndex(i => Math.min(i + 1, results.length - 1))
    } else if (e.key === "ArrowUp") {
      e.preventDefault()
      setSelectedIndex(i => Math.max(i - 1, 0))
    } else if (e.key === "Enter" && results.length > 0) {
      e.preventDefault()
      const selected = results[selectedIndex]
      onSelect(selected.id, selected.invoice_number)
    } else if (e.key === "Escape") {
      onClose()
    }
  }

  return (
    <div ref={containerRef} class="relative min-w-[140px]">
      <input
        ref={inputRef}
        type="text"
        value={query}
        onInput={e => setQuery((e.target as HTMLInputElement).value)}
        onKeyDown={handleKeyDown}
        placeholder="Search..."
        class="text-sm w-full h-7 px-2 bg-blue-50 rounded outline-none border border-blue-200 leading-7 box-border"
      />
      {results.length > 0 && (
        <ul class="absolute left-0 mt-1 w-80 bg-white border border-gray-300 rounded-md shadow-lg max-h-72 overflow-auto z-50">
          {results.map((ded, index) => (
            <li
              key={ded.id}
              class={`px-4 py-3 cursor-pointer ${index === selectedIndex ? "bg-blue-50" : "hover:bg-gray-50"}`}
              onMouseEnter={() => setSelectedIndex(index)}
              onClick={() => onSelect(ded.id, ded.invoice_number)}>
              <div class="flex justify-between items-start gap-4">
                <div class="flex flex-col min-w-0">
                  <span class="font-medium truncate">{ded.invoice_number}</span>
                  <span class="text-xs text-gray-500 truncate">{ded.source}</span>
                </div>
                <div class="flex flex-col items-end shrink-0">
                  <span class="text-gray-600">
                    {new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" }).format(ded.invoice_amount)}
                  </span>
                  <span class="text-xs text-gray-400">{kindName(ded)}</span>
                </div>
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  )
}

function formatCurrency(amount: number | undefined): string | null {
  if (amount === undefined || amount === null) return null
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
  }).format(amount)
}

interface AccountCellProps {
  row: FileImportRow
  onQuickCode: (rowId: string, account: string) => void
}

function AccountCell({ row, onQuickCode }: AccountCellProps) {
  const [editing, setEditing] = useState(false)
  const [localAccount, setLocalAccount] = useState<string | undefined>(row.display_splits?.[0]?.account)
  const [isSuggestion, setIsSuggestion] = useState(false)
  const lastPropAccount = useRef(row.display_splits?.[0]?.account)

  // Sync from props if changed externally
  const currentAccount = row.display_splits?.[0]?.account
  if (currentAccount !== lastPropAccount.current) {
    lastPropAccount.current = currentAccount
    setLocalAccount(currentAccount)
    setIsSuggestion(false)
  }

  // If no confirmed account but has suggestion, show suggestion in grey
  const suggestedAccount = row.suggested_split?.[0]?.account
  const displayAccount = localAccount || (isSuggestion ? undefined : suggestedAccount)
  const showAsSuggestion = !localAccount && suggestedAccount && !isSuggestion

  if (editing) {
    return (
      <AccountAutocomplete
        onSelect={account => {
          setLocalAccount(account.expense_account || account.name)
          setIsSuggestion(false)
          setEditing(false)
          onQuickCode(row.id, account.expense_account || account.name)
        }}
        onClose={() => setEditing(false)}
      />
    )
  }

  // Show blank if no value and no suggestion
  if (!displayAccount && !showAsSuggestion) {
    return <div class="h-6 w-full min-w-[100px] cursor-pointer" onClick={() => setEditing(true)} />
  }

  // Show suggestion in grey (clicking accepts it)
  if (showAsSuggestion) {
    return (
      <div
        class="h-6 cursor-pointer flex items-center"
        onClick={() => {
          // Accept the suggestion
          setLocalAccount(suggestedAccount)
          setIsSuggestion(false)
          onQuickCode(row.id, suggestedAccount)
        }}>
        <span class="text-sm text-gray-400 text-nowrap">{suggestedAccount}</span>
      </div>
    )
  }

  // Show confirmed account in black
  return (
    <div class="h-6 cursor-pointer flex items-center" onClick={() => setEditing(true)}>
      <span class="text-sm text-black text-nowrap">{displayAccount}</span>
    </div>
  )
}

function AccountAutocomplete({ onSelect, onClose }: { onSelect: (account: SelectItem) => void; onClose: () => void }) {
  const [query, setQuery] = useState("")
  const [selectedIndex, setSelectedIndex] = useState(0)
  const inputRef = useRef<HTMLInputElement>(null)
  const containerRef = useRef<HTMLDivElement>(null)
  const accounts = LedgerAccounts.use()

  const filteredAccounts = useMemo(() => {
    if (!query) return accounts
    const lowerQuery = query.toLowerCase()
    return accounts.filter(
      a =>
        a.name.toLowerCase().includes(lowerQuery) ||
        (a.expense_account && a.expense_account.toLowerCase().includes(lowerQuery))
    )
  }, [accounts, query])

  useEffect(() => {
    inputRef.current?.focus()
  }, [])

  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        onClose()
      }
    }
    document.addEventListener("mousedown", handleClickOutside)
    return () => document.removeEventListener("mousedown", handleClickOutside)
  }, [onClose])

  function handleKeyDown(e: KeyboardEvent) {
    if (e.key === "ArrowDown") {
      e.preventDefault()
      setSelectedIndex(i => Math.min(i + 1, filteredAccounts.length - 1))
    } else if (e.key === "ArrowUp") {
      e.preventDefault()
      setSelectedIndex(i => Math.max(i - 1, 0))
    } else if (e.key === "Enter" && filteredAccounts.length > 0) {
      e.preventDefault()
      onSelect(filteredAccounts[selectedIndex])
    } else if (e.key === "Escape") {
      onClose()
    }
  }

  return (
    <div ref={containerRef} class="relative min-w-[140px]">
      <input
        ref={inputRef}
        type="text"
        value={query}
        onInput={e => {
          setQuery((e.target as HTMLInputElement).value)
          setSelectedIndex(0)
        }}
        onKeyDown={handleKeyDown}
        placeholder="Search accounts..."
        class="text-sm w-full h-7 px-2 bg-blue-50 rounded outline-none border border-blue-200 leading-7 box-border"
      />
      {filteredAccounts.length > 0 && (
        <ul class="absolute left-0 mt-1 w-64 bg-white border border-gray-300 rounded-md shadow-lg max-h-72 overflow-auto z-50">
          {filteredAccounts.map((account, index) => (
            <li
              key={account.id}
              class={`px-4 py-2 cursor-pointer text-sm ${index === selectedIndex ? "bg-blue-50" : "hover:bg-gray-50"}`}
              onMouseEnter={() => setSelectedIndex(index)}
              onClick={() => onSelect(account)}>
              {account.expense_account || account.name}
            </li>
          ))}
        </ul>
      )}
    </div>
  )
}

function BulkAccountAutocomplete({
  onSelect,
  onClose,
}: {
  onSelect: (account: SelectItem) => void
  onClose: () => void
}) {
  const [query, setQuery] = useState("")
  const [selectedIndex, setSelectedIndex] = useState(0)
  const inputRef = useRef<HTMLInputElement>(null)
  const containerRef = useRef<HTMLDivElement>(null)
  const accounts = LedgerAccounts.use()

  const filteredAccounts = useMemo(() => {
    if (!query) return accounts
    const lowerQuery = query.toLowerCase()
    return accounts.filter(
      a =>
        a.name.toLowerCase().includes(lowerQuery) ||
        (a.expense_account && a.expense_account.toLowerCase().includes(lowerQuery))
    )
  }, [accounts, query])

  useEffect(() => {
    inputRef.current?.focus()
  }, [])

  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        onClose()
      }
    }
    document.addEventListener("mousedown", handleClickOutside)
    return () => document.removeEventListener("mousedown", handleClickOutside)
  }, [onClose])

  function handleKeyDown(e: KeyboardEvent) {
    if (e.key === "ArrowDown") {
      e.preventDefault()
      setSelectedIndex(i => Math.min(i + 1, filteredAccounts.length - 1))
    } else if (e.key === "ArrowUp") {
      e.preventDefault()
      setSelectedIndex(i => Math.max(i - 1, 0))
    } else if (e.key === "Enter" && filteredAccounts.length > 0) {
      e.preventDefault()
      onSelect(filteredAccounts[selectedIndex])
    } else if (e.key === "Escape") {
      onClose()
    }
  }

  return (
    <div ref={containerRef} class="relative">
      <input
        ref={inputRef}
        type="text"
        value={query}
        onInput={e => {
          setQuery((e.target as HTMLInputElement).value)
          setSelectedIndex(0)
        }}
        onKeyDown={handleKeyDown}
        placeholder="Search accounts..."
        class="text-sm w-48 h-8 px-3 bg-slate-800 text-white rounded outline-none border border-slate-600 focus:border-blue-500"
      />
      {filteredAccounts.length > 0 && (
        <ul class="absolute left-0 bottom-full mb-2 w-64 bg-white border border-gray-300 rounded-md shadow-lg max-h-72 overflow-auto z-50">
          {filteredAccounts.map((account, index) => (
            <li
              key={account.id}
              class={`px-4 py-2 cursor-pointer text-sm ${index === selectedIndex ? "bg-blue-50" : "hover:bg-gray-50"}`}
              onMouseEnter={() => setSelectedIndex(index)}
              onClick={() => onSelect(account)}>
              {account.expense_account || account.name}
            </li>
          ))}
        </ul>
      )}
    </div>
  )
}

type RowType = "deduction" | "invoice" | "prepayment" | "repayment" | "correction" | "unknown"

function getRowType(row: FileImportRow): RowType {
  // Use the kind field from the backend if set
  if (row.kind) {
    return row.kind
  }
  return "unknown"
}

interface FileImportStats {
  total: number
  deductions: number
  invoices: number
  prepayments: number
  repayments: number
  corrections: number
  unknown: number
  matched: number
  coded: number
}

function computeStats(rows: FileImportRow[]): FileImportStats {
  const stats: FileImportStats = {
    total: rows.length,
    deductions: 0,
    invoices: 0,
    prepayments: 0,
    repayments: 0,
    corrections: 0,
    unknown: 0,
    matched: 0,
    coded: 0,
  }

  for (const row of rows) {
    const rowType = getRowType(row)
    if (rowType === "deduction") stats.deductions++
    else if (rowType === "invoice") stats.invoices++
    else if (rowType === "prepayment") stats.prepayments++
    else if (rowType === "repayment") stats.repayments++
    else if (rowType === "correction") stats.corrections++
    else stats.unknown++

    // Count matched deductions (only deduction-type rows)
    if (rowType === "deduction" && row.deduction_id) stats.matched++

    // Count coded rows (have splits)
    if (row.display_splits && row.display_splits.length > 0) stats.coded++
  }

  return stats
}

function rowMatchesFilter(row: FileImportRow, filter: FilterState): boolean {
  if (filter.row_ids != null && filter.row_ids.size > 0 && !filter.row_ids.has(row.id)) {
    return false
  }

  if (isFilterEmpty(filter)) return true

  const rowType = getRowType(row)

  // Check kind filter (if any kinds are selected, row must match one of them)
  if (filter.kinds.size > 0) {
    if (!filter.kinds.has(rowType as KindFilter)) {
      return false
    }
  }

  // Check matching filter
  if (filter.matching === "matched" && !row.deduction_id) {
    return false
  }
  if (filter.matching === "unmatched" && (rowType !== "deduction" || row.deduction_id)) {
    return false
  }

  // Check coding filter
  if (filter.coding === "coded" && (!row.display_splits || row.display_splits.length === 0)) {
    return false
  }
  if (filter.coding === "uncoded" && row.display_splits && row.display_splits.length > 0) {
    return false
  }

  return true
}

function filterRows(row: { original: FileImportRow }, _columnId: string, filterValue: FilterState): boolean {
  return rowMatchesFilter(row.original, filterValue)
}

interface StatisticsBoxProps {
  rows: FileImportRow[]
  activeFilter: FilterState
  onFilterChange: (filter: FilterState) => void
}

function StatisticsBar({ rows, activeFilter, onFilterChange }: StatisticsBoxProps) {
  // Compute stats for the current filter context
  // For kind chips: apply only matching + coding filters to get counts
  // For matching chips: apply only kind + coding filters to get counts
  // For coding chips: apply only kind + matching filters to get counts
  const totalStats = useMemo(() => computeStats(rows), [rows])
  const hasActiveFilter = !isFilterEmpty(activeFilter)
  const importResultRowFilterCount = activeFilter.row_ids?.size ?? 0

  // Filter rows for counting in each section
  const kindFilteredRows = useMemo(() => {
    const filterWithoutKinds: FilterState = {
      kinds: new Set(),
      matching: activeFilter.matching,
      coding: activeFilter.coding,
      row_ids: activeFilter.row_ids,
    }
    return rows.filter(r => rowMatchesFilter(r, filterWithoutKinds))
  }, [rows, activeFilter.matching, activeFilter.coding, activeFilter.row_ids])

  const matchingFilteredRows = useMemo(() => {
    const filterWithoutMatching: FilterState = {
      kinds: activeFilter.kinds,
      matching: null,
      coding: activeFilter.coding,
      row_ids: activeFilter.row_ids,
    }
    return rows.filter(r => rowMatchesFilter(r, filterWithoutMatching))
  }, [rows, activeFilter.kinds, activeFilter.coding, activeFilter.row_ids])

  const codingFilteredRows = useMemo(() => {
    const filterWithoutCoding: FilterState = {
      kinds: activeFilter.kinds,
      matching: activeFilter.matching,
      coding: null,
      row_ids: activeFilter.row_ids,
    }
    return rows.filter(r => rowMatchesFilter(r, filterWithoutCoding))
  }, [rows, activeFilter.kinds, activeFilter.matching, activeFilter.row_ids])

  // Stats for each section
  const kindStats = useMemo(() => computeStats(kindFilteredRows), [kindFilteredRows])
  const matchingStats = useMemo(() => computeStats(matchingFilteredRows), [matchingFilteredRows])
  const codingStats = useMemo(() => computeStats(codingFilteredRows), [codingFilteredRows])

  // Current filtered count
  const filteredCount = useMemo(() => rows.filter(r => rowMatchesFilter(r, activeFilter)).length, [rows, activeFilter])

  function handleKindClick(kind: KindFilter) {
    const newKinds = new Set(activeFilter.kinds)
    if (newKinds.has(kind)) {
      newKinds.delete(kind)
    } else {
      newKinds.add(kind)
    }
    onFilterChange({ ...activeFilter, kinds: newKinds })
  }

  function handleMatchingClick(matching: MatchingFilter) {
    const newMatching = activeFilter.matching === matching ? null : matching
    onFilterChange({ ...activeFilter, matching: newMatching })
  }

  function handleCodingClick(coding: CodingFilter) {
    const newCoding = activeFilter.coding === coding ? null : coding
    onFilterChange({ ...activeFilter, coding: newCoding })
  }

  function handleResetFilters() {
    onFilterChange(createEmptyFilter())
  }

  function KindChip({ label, value, kind }: { label: string; value: number; kind: KindFilter }) {
    const isActive = activeFilter.kinds.has(kind)
    return (
      <button
        onClick={() => handleKindClick(kind)}
        class={`px-3 py-1.5 rounded-full text-sm whitespace-nowrap transition-colors ${
          isActive ? "bg-blue-600 text-white" : "bg-gray-100 text-gray-700 hover:bg-gray-200"
        }`}>
        {label} <span class="font-semibold">{value}</span>
      </button>
    )
  }

  function MatchingChip({ label, value, matching }: { label: string; value: number; matching: MatchingFilter }) {
    const isActive = activeFilter.matching === matching
    return (
      <button
        onClick={() => handleMatchingClick(matching)}
        class={`px-3 py-1.5 rounded-full text-sm whitespace-nowrap transition-colors ${
          isActive ? "bg-blue-600 text-white" : "bg-gray-100 text-gray-700 hover:bg-gray-200"
        }`}>
        {label} <span class="font-semibold">{value}</span>
      </button>
    )
  }

  function CodingChip({ label, value, coding }: { label: string; value: number; coding: CodingFilter }) {
    const isActive = activeFilter.coding === coding
    return (
      <button
        onClick={() => handleCodingClick(coding)}
        class={`px-3 py-1.5 rounded-full text-sm whitespace-nowrap transition-colors ${
          isActive ? "bg-blue-600 text-white" : "bg-gray-100 text-gray-700 hover:bg-gray-200"
        }`}>
        {label} <span class="font-semibold">{value}</span>
      </button>
    )
  }

  function Divider() {
    return <div class="w-px h-6 bg-gray-300" />
  }

  return (
    <div class="bg-white rounded-lg shadow px-4 py-3 mb-4 space-y-3">
      {importResultRowFilterCount > 0 ? (
        <div class="flex flex-wrap items-center gap-2 rounded-md border border-indigo-200 bg-indigo-50 px-3 py-2 text-sm text-indigo-950">
          <span>
            Showing <span class="font-semibold">{importResultRowFilterCount}</span> row
            {importResultRowFilterCount === 1 ? "" : "s"} from import results
          </span>
          <button
            type="button"
            onClick={() => onFilterChange({ ...activeFilter, row_ids: null })}
            class="text-indigo-700 hover:text-indigo-900 underline font-medium">
            Clear row filter
          </button>
        </div>
      ) : null}
      <div class="flex items-center gap-2 flex-wrap">
        <button
          onClick={handleResetFilters}
          class={`px-3 py-1.5 rounded-full text-sm whitespace-nowrap transition-colors min-w-[8rem] ${
            !hasActiveFilter ? "bg-blue-600 text-white" : "bg-gray-100 text-gray-700 hover:bg-gray-200"
          }`}>
          Total{" "}
          <span class="font-semibold">
            {hasActiveFilter ? `${filteredCount} of ${totalStats.total}` : totalStats.total}
          </span>
        </button>
        <Divider />
        <span class="text-xs text-gray-500 uppercase">Types:</span>
        <KindChip label="Deductions" value={kindStats.deductions} kind="deduction" />
        <KindChip label="Invoices" value={kindStats.invoices} kind="invoice" />
        <KindChip label="Prepayments" value={kindStats.prepayments} kind="prepayment" />
        <KindChip label="Repayments" value={kindStats.repayments} kind="repayment" />
        <KindChip label="Corrections" value={kindStats.corrections} kind="correction" />
        {kindStats.unknown > 0 && <KindChip label="Unknown" value={kindStats.unknown} kind="unknown" />}
        <Divider />
        <span class="text-xs text-gray-500 uppercase">Matching:</span>
        <MatchingChip label="Matched" value={matchingStats.matched} matching="matched" />
        <MatchingChip label="Unmatched" value={matchingStats.deductions - matchingStats.matched} matching="unmatched" />
        <Divider />
        <span class="text-xs text-gray-500 uppercase">Coding:</span>
        <CodingChip label="Coded" value={codingStats.coded} coding="coded" />
        <CodingChip label="Uncoded" value={codingStats.total - codingStats.coded} coding="uncoded" />
      </div>
    </div>
  )
}

const PAGE_SIZE = 50

export function FileImportDetailPage({ params }: FileImportDetailProps) {
  const [fileImport, setFileImport] = useState<FileImport | null>(null)
  const [rows, setRows] = useState<FileImportRow[]>([])
  const [loading, setLoading] = useState(true)
  const [sorting, setSorting] = useState<SortingState>([])
  const [pagination, setPagination] = useState<PaginationState>({ pageIndex: 0, pageSize: PAGE_SIZE })
  const [rowSelection, setRowSelection] = useState<Record<string, boolean>>({})
  const [expandedRowId, setExpandedRowId] = useState<string | null>(null)
  const activeFilter = FileImportFilterState.use()
  const [exporting, setExporting] = useState(false)
  const [importing, setImporting] = useState(false)
  const [importDeductionSummary, setImportDeductionSummary] = useState<ImportDeductionsResponse | null>(null)
  const [bulkCodingOpen, setBulkCodingOpen] = useState(false)
  const refreshFlag = RefreshFlag.use()

  const stats = useMemo(() => computeStats(rows), [rows])

  const bc_sot_import_preview = useMemo(() => {
    if (fileImport?.import_format !== "bc_sot_detail") {
      return null
    }
    const eligible = rows.filter(r => r.bc_sot_import_group != null && !r.deduction_id)
    if (eligible.length === 0) {
      return null
    }
    const deductions = new Set(eligible.map(r => r.bc_sot_import_group!)).size
    const multi_row_groups = new Set(
      eligible.filter(r => (r.bc_sot_import_group_rows ?? 0) > 1).map(r => r.bc_sot_import_group!),
    ).size
    const skipped_shape = rows.filter(r => !r.deduction_id && r.bc_sot_import_group == null).length
    return { importable_rows: eligible.length, deductions, multi_row_groups, skipped_shape }
  }, [rows, fileImport?.import_format])

  useAsyncEffect(async () => {
    const [file, fileRows] = await Promise.all([getFileImport(params.id), getFileImportRows(params.id)])
    setFileImport(file)
    setRows(fileRows)
    setLoading(false)
  }, [params.id, refreshFlag])

  const columns: ColumnDef<FileImportRow>[] = useMemo(() => {
    const headers = fileImport?.headers || []
    const is_bc_sot = fileImport?.import_format === "bc_sot_detail"
    const is_blue_planner = fileImport?.import_format === "blue_planner_html"
    const cityIndex = headers.findIndex(h => h.trim().toLowerCase() === "city")
    const stateIndex = headers.findIndex(h => h.trim().toLowerCase() === "state")
    const classIndex = headers.findIndex(h => h.toLowerCase() === "class")
    const classNameIndex = headers.findIndex(h => h.toLowerCase() === "class name")
    const hasClassColumn = classIndex !== -1

    const selectColumn: ColumnDef<FileImportRow> = {
      id: "select",
      header: ({ table }) => (
        <Checkbox
          checked={table.getIsAllPageRowsSelected()}
          onCheckedChange={value => table.toggleAllPageRowsSelected(!!value)}
        />
      ),
      cell: ({ row }) => (
        <Checkbox checked={row.getIsSelected()} onCheckedChange={value => row.toggleSelected(!!value)} />
      ),
      enableSorting: false,
    }

    const importGroupColumn: ColumnDef<FileImportRow> | null = is_bc_sot
      ? {
          id: "bc_sot_import_group",
          accessorFn: (row: FileImportRow) => row.bc_sot_import_group ?? 0,
          header: ({ column }) => <ColumnHeader column={column} title="Import group" />,
          cell: ({ row }) => {
            const r = row.original
            if (r.deduction_id) {
              return <span class="text-xs text-gray-400">Imported</span>
            }
            const g = r.bc_sot_import_group
            const n = r.bc_sot_import_group_rows
            if (g == null || n == null) {
              return (
                <span
                  class="text-xs text-amber-700"
                  title="Missing invoice, kind, or source — this row will not import with a merge group.">
                  —
                </span>
              )
            }
            return (
              <div
                class="flex items-center gap-1.5 whitespace-nowrap"
                title={
                  n > 1
                    ? `${n} file rows with this group number become one deduction on import.`
                    : "This row becomes its own deduction on import."
                }>
                <span
                  class={`text-xs font-semibold tabular-nums px-1.5 py-0.5 rounded ${
                    n > 1 ? "bg-indigo-100 text-indigo-900" : "bg-gray-100 text-gray-700"
                  }`}>
                  #{g}
                </span>
                {n > 1 ? <span class="text-xs text-gray-500">{n} rows → 1</span> : null}
              </div>
            )
          },
        }
      : null

    const csvColumns: ColumnDef<FileImportRow>[] = headers
      .map((header, index) => ({ header, index }))
      .filter(({ header }) => {
        const h = header.trim().toLowerCase()
        // Blue Planner: file "Invoice Number" is accounting id; real invoice is Attribute2 on `invoice_number`.
        if (is_blue_planner && h === "invoice number") {
          return false
        }
        // Blue Planner: City + State are merged into DC Name (system column).
        if (is_blue_planner && (h === "city" || h === "state")) {
          return false
        }
        return true
      })
      .map(({ header, index }) => {
      const isMemo = header.toLowerCase().includes("memo")
      const isClass = index === classIndex
      const isClassName = index === classNameIndex

      // If this is the Class column and it exists, make it editable like AccountCell
      if (isClass) {
        return {
          id: `csv_${index}`,
          accessorFn: row => row.display_splits?.[0]?.account ?? "",
          header: ({ column }) => <ColumnHeader column={column} title={header} />,
          cell: ({ row, table }) => (
            <AccountCell row={row.original} onQuickCode={(table.options.meta as any)?.quickCode} />
          ),
        }
      }

      // If this is the Class name column and Class column exists, derive from selected account
      if (isClassName && hasClassColumn) {
        return {
          id: `csv_${index}`,
          accessorFn: row => row.display_splits?.[0]?.account ?? "",
          header: ({ column }) => <ColumnHeader column={column} title={header} />,
          cell: ({ row }) => {
            const account = row.original.display_splits?.[0]?.account
            const displayValue = account ? account.split(":").pop()?.trim() : undefined
            return <CopyableCell value={displayValue} wide={false} />
          },
        }
      }

      return {
        id: `csv_${index}`,
        accessorFn: row => row.row_data?.[index] ?? "",
        header: ({ column }) => <ColumnHeader column={column} title={header} />,
        cell: ({ row }) => <CopyableCell value={row.original.row_data?.[index]} wide={isMemo} />,
      }
    })

    function merged_dc_name(row: FileImportRow): string {
      const c = cityIndex >= 0 ? row.row_data?.[cityIndex]?.trim() ?? "" : ""
      const s = stateIndex >= 0 ? row.row_data?.[stateIndex]?.trim() ?? "" : ""
      if (c && s) {
        return `${c}, ${s}`
      }
      return c || s
    }

    const invoiceNumberColumn: ColumnDef<FileImportRow> = {
      accessorKey: "invoice_number",
      header: ({ column }) => <ColumnHeader column={column} title="Invoice Number" />,
      cell: ({ row, table }) => (
        <EditableCell
          value={row.original.invoice_number}
          rowId={row.original.id}
          field="invoice_number"
          onSave={(table.options.meta as any)?.updateRow}
        />
      ),
    }

    const dateColumn: ColumnDef<FileImportRow> = {
      accessorKey: "date",
      header: ({ column }) => <ColumnHeader column={column} title="Date" />,
      cell: ({ row }) =>
        row.original.date ? <span class="text-sm">{format(parseISO(row.original.date), "MM/dd/yyyy")}</span> : null,
    }

    const kindColumn: ColumnDef<FileImportRow> = {
      accessorKey: "kind",
      header: ({ column }) => <ColumnHeader column={column} title="Kind" />,
      cell: ({ row, table }) => (
        <EditableKindCell
          value={row.original.kind}
          rowId={row.original.id}
          onSave={(table.options.meta as any)?.updateKind}
        />
      ),
    }

    const dcNameColumn: ColumnDef<FileImportRow> | null = is_blue_planner
      ? {
          id: "dc_name",
          accessorFn: row => merged_dc_name(row),
          header: ({ column }) => <ColumnHeader column={column} title="DC Name" />,
          cell: ({ row }) => {
            const merged = merged_dc_name(row.original)
            return merged ? <CopyableCell value={merged} wide={false} /> : null
          },
        }
      : null

    const tailSystemColumns: ColumnDef<FileImportRow>[] = [
      {
        accessorKey: "debit_amount",
        header: ({ column }) => <ColumnHeader column={column} title="Debit" />,
        cell: ({ row }) => <span class="text-sm">{formatCurrency(row.original.debit_amount)}</span>,
      },
      {
        accessorKey: "credit_amount",
        header: ({ column }) => <ColumnHeader column={column} title="Credit" />,
        cell: ({ row }) => <span class="text-sm">{formatCurrency(row.original.credit_amount)}</span>,
      },
      {
        id: "match",
        accessorFn: row => (row.deduction_id ? "linked" : row.suggested_deduction_id ? "suggested" : ""),
        header: ({ column }) => <ColumnHeader column={column} title="Match" />,
        cell: ({ row, table }) => {
          if (row.original.suggested_deduction_id && !row.original.deduction_id) {
            return (
              <div class="flex items-center gap-2 whitespace-nowrap">
                <a
                  href={`/deduction/${row.original.suggested_deduction_id}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  class="text-sm text-gray-500 hover:text-gray-700 italic">
                  Suggested
                </a>
                <button
                  class="text-xs text-blue-600 hover:text-blue-800"
                  onClick={() =>
                    (table.options.meta as any)?.linkDeduction(row.original.id, row.original.suggested_deduction_id)
                  }>
                  Accept
                </button>
              </div>
            )
          }
          return (
            <MatchAutocomplete
              deductionId={row.original.deduction_id}
              matchedInvoiceNumber={row.original.matched_invoice_number}
              rowId={row.original.id}
              onSelect={(table.options.meta as any)?.linkDeduction}
            />
          )
        },
      },
      {
        id: "promotion",
        accessorFn: row => row.matched_promotion_name ?? "",
        header: ({ column }) => <ColumnHeader column={column} title="Promotion" />,
        cell: ({ row }) => {
          if (!row.original.deduction_id) return null
          if (row.original.matched_promotion_id) {
            return (
              <a
                href={`/trade-promotion-management?promotion=${row.original.matched_promotion_id}`}
                target="_blank"
                rel="noopener noreferrer"
                class="inline-flex items-center gap-1 whitespace-nowrap rounded-full bg-green-50 px-2 py-0.5 text-xs text-green-700 hover:bg-green-100"
                title={`Matched to promotion: ${row.original.matched_promotion_name}`}>
                <Megaphone className="h-3 w-3 shrink-0" />
                <span class="max-w-[140px] truncate">{row.original.matched_promotion_name}</span>
              </a>
            )
          }
          return <span class="whitespace-nowrap text-xs text-gray-400">No promo</span>
        },
      },
      {
        id: "account",
        accessorFn: row => row.display_splits?.[0]?.account ?? "",
        header: ({ column }) => <ColumnHeader column={column} title="Account" />,
        cell: ({ row, table }) => (
          <AccountCell row={row.original} onQuickCode={(table.options.meta as any)?.quickCode} />
        ),
      },
    ]

    // Filter out Account column if Class column exists (Class column takes its place)
    const filteredTailSystemColumns = hasClassColumn
      ? tailSystemColumns.filter(col => col.id !== "account")
      : tailSystemColumns

    const leadColumns: ColumnDef<FileImportRow>[] = dcNameColumn
      ? [invoiceNumberColumn, dateColumn, kindColumn, dcNameColumn]
      : [invoiceNumberColumn, dateColumn, kindColumn]

    return [
      selectColumn,
      ...(importGroupColumn ? [importGroupColumn] : []),
      ...leadColumns,
      ...csvColumns,
      ...filteredTailSystemColumns,
    ]
  }, [fileImport?.headers, fileImport?.import_format])

  async function handleUpdateRow(rowId: string, field: string, value: string) {
    await updateFileImportRow(rowId, { [field]: value })
    triggerRefreshFlag()
  }

  async function handleLinkDeduction(rowId: string, deductionId: string) {
    await updateFileImportRow(rowId, { deduction_id: deductionId })
    triggerRefreshFlag()
  }

  async function handleUpdateKind(rowId: string, kind: TransactionKind | null) {
    await updateFileImportRow(rowId, { kind })
    triggerRefreshFlag()
  }

  async function handleQuickCode(rowId: string, account: string) {
    const row = rows.find(r => r.id === rowId)
    if (!row) return
    const netAmount = row.net_amount ?? 0
    await updateFileImportRow(rowId, { splits: [{ account, amount: Math.abs(netAmount) }] })
    triggerRefreshFlag()
  }

  async function handleSaveSplits(rowId: string, splits: Split[]) {
    await updateFileImportRow(rowId, { splits })
    setExpandedRowId(null)
    triggerRefreshFlag()
  }

  async function handleBulkCode(account: string) {
    const selectedRowIds = Object.keys(rowSelection).filter(id => rowSelection[id])
    await Promise.all(
      selectedRowIds.map(async rowId => {
        const row = rows.find(r => r.id === rowId)
        if (!row) return
        const netAmount = row.net_amount ?? 0
        await updateFileImportRow(rowId, { splits: [{ account, amount: Math.abs(netAmount) }] })
      })
    )
    setRowSelection({})
    setBulkCodingOpen(false)
    triggerRefreshFlag()
  }

  async function handleExport() {
    setExporting(true)
    try {
      const response = await fetch(getFileImportExportUrl(params.id), {
        credentials: "include",
      })
      if (!response.ok) {
        throw new Error("Export failed")
      }
      const blob = await response.blob()
      const contentDisposition = response.headers.get("Content-Disposition")
      let filename = "export.csv"
      if (contentDisposition) {
        const match = contentDisposition.match(/filename="?([^"]+)"?/)
        if (match) {
          filename = match[1]
        }
      }
      const url = URL.createObjectURL(blob)
      const a = document.createElement("a")
      a.href = url
      a.download = filename
      document.body.appendChild(a)
      a.click()
      document.body.removeChild(a)
      URL.revokeObjectURL(url)
    } finally {
      setExporting(false)
    }
  }

  async function handleImportDeductions() {
    setImporting(true)
    try {
      const selectedRowIds = Object.keys(rowSelection).filter(id => rowSelection[id])
      // If rows are selected, import only those; otherwise import all
      const payload = selectedRowIds.length > 0 ? { row_ids: selectedRowIds } : {}
      const result = await importDeductions(params.id, payload)
      setImportDeductionSummary(result)
      const linked = result.rows_linked_count
      const deds = result.imported_count
      const skipped = result.skipped_count
      const parts = [
        `${deds} deduction${deds === 1 ? "" : "s"} created`,
        linked !== deds ? `${linked} row${linked === 1 ? "" : "s"} linked` : null,
        skipped > 0 ? `${skipped} skipped` : null,
      ].filter(Boolean)
      toast({
        title: "Import complete",
        description: `${parts.join(" · ")} — see Import results for each row.`,
      })
      setRowSelection({})
      triggerRefreshFlag()
    } finally {
      setImporting(false)
    }
  }

  const table = useReactTable({
    data: rows,
    columns,
    state: { sorting, pagination, rowSelection, globalFilter: activeFilter },
    initialState: {
      columnPinning: {
        left: ["select"],
      },
    },
    onSortingChange: setSorting,
    onPaginationChange: setPagination,
    onRowSelectionChange: setRowSelection,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    globalFilterFn: filterRows,
    getRowId: row => row.id,
    autoResetPageIndex: false,
    meta: {
      updateRow: handleUpdateRow,
      linkDeduction: handleLinkDeduction,
      updateKind: handleUpdateKind,
      quickCode: handleQuickCode,
    },
  })

  if (loading) {
    return <CenterSpinner />
  }

  if (!fileImport) {
    return <div class="p-6">File import not found</div>
  }

  const selectedCount = Object.keys(rowSelection).length
  const tableRows = table.getRowModel().rows
  const filteredRowCount = table.getFilteredRowModel().rows.length

  return (
    <div class="w-full">
      <div class="mb-6 px-4 flex justify-between items-start">
        <div>
          <div class="flex items-center gap-2 mb-1">
            <h1 class="text-2xl font-bold">{fileImport.s3_url.split("/").pop()}</h1>
            <a
              href={getFileImportDownloadUrl(fileImport.s3_url)}
              class="p-1.5 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded"
              title="Download original file">
              <Download size={18} />
            </a>
          </div>
          <p class="text-sm text-gray-600">Uploaded {format(parseISO(fileImport.created_at), "MMM d, yyyy h:mm a")}</p>
          {fileImport.import_format ? (
            <p class="text-xs text-gray-500 mt-1">
              Detected format:{" "}
              <span class="font-medium">{label_for_file_import_format(fileImport.import_format)}</span>
            </p>
          ) : null}
          {bc_sot_import_preview ? (
            <p class="text-xs text-gray-600 mt-1 max-w-3xl leading-relaxed">
              Import preview:{" "}
              <span class="font-semibold text-gray-800">{bc_sot_import_preview.deductions}</span> deduction
              {bc_sot_import_preview.deductions === 1 ? "" : "s"} from{" "}
              <span class="font-semibold text-gray-800">{bc_sot_import_preview.importable_rows}</span> importable row
              {bc_sot_import_preview.importable_rows === 1 ? "" : "s"}
              {bc_sot_import_preview.multi_row_groups > 0
                ? ` (${bc_sot_import_preview.multi_row_groups} group${
                    bc_sot_import_preview.multi_row_groups === 1 ? "" : "s"
                  } combine multiple lines)`
                : ""}
              . Same <span class="font-semibold">Import group</span> number → one deduction.
              {bc_sot_import_preview.skipped_shape > 0
                ? ` ${bc_sot_import_preview.skipped_shape} row${
                    bc_sot_import_preview.skipped_shape === 1 ? "" : "s"
                  } missing invoice/kind/source (not in a group).`
                : ""}
            </p>
          ) : null}
          <FileImportHelp
            className="mt-4 max-w-3xl"
            defaultOpen={false}
            detectedFormat={fileImport.import_format}
          />
        </div>
        <div class="flex gap-2">
          <Button variant="outline" onClick={handleImportDeductions} disabled={importing} class="cursor-pointer">
            <Upload size={16} className="mr-2 inline" />
            {importing
              ? "Importing..."
              : selectedCount > 0
                ? `Import ${selectedCount} Deduction${selectedCount > 1 ? "s" : ""}`
                : "Import Deductions"}
          </Button>
          <Button variant="outline" onClick={handleExport} disabled={exporting} class="cursor-pointer">
            <Download size={16} className="mr-2 inline" />
            {exporting ? "Exporting..." : "Export Coding"}
          </Button>
        </div>
      </div>

      <StatisticsBar
        rows={rows}
        activeFilter={activeFilter}
        onFilterChange={f => {
          FileImportFilterState.set(f)
          setPagination(p => ({ ...p, pageIndex: 0 }))
        }}
      />

      <div class="bg-white rounded-lg shadow overflow-x-auto mb-20">
        <Table class="border-separate border-spacing-0">
          <TableHeader>
            {table.getHeaderGroups().map(headerGroup => (
              <TableRow key={headerGroup.id}>
                {headerGroup.headers.map(header => {
                  const isPinned = header.column.getIsPinned()
                  return (
                    <TableHead
                      key={header.id}
                      class={`px-6 py-3 whitespace-nowrap ${
                        isPinned === "left"
                          ? "sticky left-0 bg-white z-20 shadow-[2px_0_4px_-2px_rgba(0,0,0,0.1)]"
                          : ""
                      }`}>
                      {header.isPlaceholder ? null : flexRender(header.column.columnDef.header, header.getContext())}
                    </TableHead>
                  )
                })}
              </TableRow>
            ))}
          </TableHeader>
          <TableBody>
            {tableRows.length === 0 ? (
              <TableRow>
                <TableCell colSpan={columns.length} class="h-24 text-center">
                  No rows found.
                </TableCell>
              </TableRow>
            ) : (
              tableRows.map(row => {
                const isExpanded = expandedRowId === row.original.id
                return (
                  <Fragment key={row.id}>
                    <TableRow
                      data-state={row.getIsSelected() && "selected"}
                      onClick={e => {
                        const target = e.target as HTMLElement
                        if (target.closest('input, select, button, a, [role="checkbox"]')) {
                          return
                        }
                        if (table.getIsSomeRowsSelected()) {
                          row.toggleSelected()
                        }
                      }}
                      class={`${table.getIsSomeRowsSelected() ? "cursor-pointer" : ""} ${
                        fileImport?.import_format === "bc_sot_detail"
                          ? bc_sot_import_row_left_border_class(row.original)
                          : ""
                      }`}>
                      {row.getVisibleCells().map(cell => {
                        const isPinned = cell.column.getIsPinned()
                        return (
                          <TableCell
                            key={cell.id}
                            class={`px-6 py-3 whitespace-nowrap ${
                              isPinned === "left"
                                ? "sticky left-0 bg-white z-10 shadow-[2px_0_4px_-2px_rgba(0,0,0,0.1)]"
                                : ""
                            }`}>
                            {flexRender(cell.column.columnDef.cell, cell.getContext())}
                          </TableCell>
                        )
                      })}
                    </TableRow>
                    {isExpanded && (
                      <InlineSplitEditor
                        row={row.original}
                        onClose={() => setExpandedRowId(null)}
                        onSave={splits => handleSaveSplits(row.original.id, splits)}
                        colSpan={columns.length}
                      />
                    )}
                  </Fragment>
                )
              })
            )}
          </TableBody>
        </Table>

        {/* Pagination controls */}
        <div class="flex items-center justify-between px-4 py-3 border-t">
          <div class="text-sm text-gray-600">
            {filteredRowCount === 0
              ? "No rows"
              : `Showing ${pagination.pageIndex * PAGE_SIZE + 1} to ${Math.min((pagination.pageIndex + 1) * PAGE_SIZE, filteredRowCount)} of ${filteredRowCount} rows`}
            {!isFilterEmpty(activeFilter) && ` (filtered from ${rows.length} total)`}
          </div>
          <div class="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => table.setPageIndex(0)}
              disabled={!table.getCanPreviousPage()}>
              First
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => table.previousPage()}
              disabled={!table.getCanPreviousPage()}>
              Previous
            </Button>
            <span class="text-sm text-gray-600 px-2">
              Page {pagination.pageIndex + 1} of {table.getPageCount()}
            </span>
            <Button variant="outline" size="sm" onClick={() => table.nextPage()} disabled={!table.getCanNextPage()}>
              Next
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => table.setPageIndex(table.getPageCount() - 1)}
              disabled={!table.getCanNextPage()}>
              Last
            </Button>
          </div>
        </div>
      </div>

      {selectedCount > 0 && (
        <div class="fixed bottom-10 left-1/2 transform -translate-x-1/2 z-10 bg-slate-900 rounded-lg border border-slate-700 p-2 flex items-center gap-3">
          <button
            onClick={() => setRowSelection({})}
            class="flex items-center gap-1 text-gray-400 hover:text-white px-2 py-1">
            <span>{selectedCount} selected</span>
            <X size={16} />
          </button>
          <div class="w-px h-8 bg-slate-700" />
          {bulkCodingOpen ? (
            <BulkAccountAutocomplete
              onSelect={account => handleBulkCode(account.expense_account || account.name)}
              onClose={() => setBulkCodingOpen(false)}
            />
          ) : (
            <Button size="sm" class="bg-blue-600 hover:bg-blue-700 text-white" onClick={() => setBulkCodingOpen(true)}>
              Code Selected
            </Button>
          )}
        </div>
      )}

      <ImportDeductionResultsDialog
        result={importDeductionSummary}
        rows={rows}
        headers={fileImport.headers ?? []}
        onOpenChange={(open: boolean) => {
          if (!open) {
            setImportDeductionSummary(null)
          }
        }}
        onViewRowsInTable={(row_ids: string[]) => {
          FileImportFilterState.set(filter_for_import_result_row_ids(row_ids))
          setPagination(p => ({ ...p, pageIndex: 0 }))
          setImportDeductionSummary(null)
        }}
      />
    </div>
  )
}

interface InlineSplitEditorProps {
  row: FileImportRow
  onClose: () => void
  onSave: (splits: Split[]) => void
  colSpan: number
}

function InlineSplitEditor({ row, onClose, onSave, colSpan }: InlineSplitEditorProps) {
  const netAmount = row.net_amount ?? 0
  const [splits, setSplits] = useState<Split[]>(
    row.display_splits && row.display_splits.length > 0
      ? row.display_splits.map(s => ({ account: s.account, amount: s.amount, description: s.description }))
      : [{ account: "", amount: netAmount, description: "" }]
  )
  const [saving, setSaving] = useState(false)
  const [editingIndex, setEditingIndex] = useState<number | null>(
    row.display_splits && row.display_splits.length > 0 ? null : 0
  )

  function addSplit() {
    // Calculate remaining amount
    const totalCents = splits.reduce((sum, s) => sum + Math.round((Number(s.amount) || 0) * 100), 0)
    const netCents = Math.round(netAmount * 100)
    const remainingCents = netCents - totalCents
    const remainingAmount = remainingCents / 100

    const newIndex = splits.length
    setSplits([...splits, { account: "", amount: remainingAmount, description: "" }])
    setEditingIndex(newIndex)
  }

  function updateSplit(index: number, field: keyof Split, value: string | number) {
    const updated = [...splits]
    updated[index] = { ...updated[index], [field]: value }
    setSplits(updated)
  }

  function removeSplit(index: number) {
    setSplits(splits.filter((_, i) => i !== index))
    if (editingIndex === index) {
      setEditingIndex(null)
    } else if (editingIndex !== null && editingIndex > index) {
      setEditingIndex(editingIndex - 1)
    }
  }

  async function handleSave() {
    setSaving(true)
    try {
      await onSave(splits)
    } finally {
      setSaving(false)
    }
  }

  const totalAmount = splits.reduce((sum, s) => sum + (Number(s.amount) || 0), 0)
  const totalCents = Math.round(totalAmount * 100)
  const netCents = Math.round(netAmount * 100)
  const differenceCents = Math.abs(totalCents - netCents)

  return (
    <tr>
      <td colSpan={colSpan} class="p-0">
        <div class="bg-gray-50 border-t border-b border-gray-200 p-4">
          {/* Header */}
          <div class="flex items-center justify-between mb-4">
            <div class="flex items-center gap-4">
              <span class="text-sm font-medium text-gray-700">Transaction: {formatCurrency(netAmount)}</span>
              {row.split_source === "deduction" && (
                <span class="text-xs text-blue-600 bg-blue-50 px-2 py-1 rounded">From linked deduction</span>
              )}
            </div>
            <div class="flex items-center gap-2">
              <Button variant="outline" size="sm" onClick={addSplit}>
                <Plus size={14} className="mr-1" />
                Add Split
              </Button>
              <Button size="sm" onClick={handleSave} disabled={saving}>
                {saving ? "Saving..." : "Save"}
              </Button>
              <button onClick={onClose} class="text-gray-400 hover:text-gray-600 ml-2">
                <X size={18} />
              </button>
            </div>
          </div>

          {/* Warning if amounts don't match */}
          {differenceCents > 1 && (
            <div class="mb-4 p-3 bg-yellow-50 border-l-4 border-yellow-400 rounded-r">
              <p class="text-sm text-yellow-800">
                Split total ({formatCurrency(totalAmount)}) differs from transaction amount by{" "}
                {formatCurrency(differenceCents / 100)}
              </p>
            </div>
          )}

          {/* Split cards */}
          <div class="space-y-2">
            {splits.length === 0 ? (
              <div class="text-center py-6 text-gray-500 text-sm">No splits yet. Click "Add Split" to get started.</div>
            ) : (
              splits.map((split, index) => (
                <SplitCard
                  key={index}
                  split={split}
                  index={index}
                  isEditing={editingIndex === index}
                  onUpdate={(field, value) => updateSplit(index, field, value)}
                  onDelete={() => removeSplit(index)}
                  onStartEdit={() => setEditingIndex(index)}
                  onStopEdit={() => setEditingIndex(null)}
                />
              ))
            )}
          </div>
        </div>
      </td>
    </tr>
  )
}

interface SplitCardProps {
  split: Split
  index: number
  isEditing: boolean
  onUpdate: (field: keyof Split, value: string | number) => void
  onDelete: () => void
  onStartEdit: () => void
  onStopEdit: () => void
}

function SplitCard({ split, index, isEditing, onUpdate, onDelete, onStartEdit, onStopEdit }: SplitCardProps) {
  if (isEditing) {
    return (
      <div class="bg-white border border-blue-200 rounded-lg p-4">
        <div class="flex gap-4 items-start">
          <div class="flex-1">
            <label class="block text-xs font-medium text-gray-600 mb-1">Account</label>
            <input
              type="text"
              value={split.account}
              onInput={e => onUpdate("account", (e.target as HTMLInputElement).value)}
              class="w-full px-3 py-2 text-sm border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="Account name"
              autoFocus
            />
          </div>
          <div class="w-32">
            <label class="block text-xs font-medium text-gray-600 mb-1">Amount</label>
            <input
              type="number"
              step="0.01"
              value={split.amount}
              onInput={e => onUpdate("amount", parseFloat((e.target as HTMLInputElement).value) || 0)}
              class="w-full px-3 py-2 text-sm border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
          <div class="flex-1">
            <label class="block text-xs font-medium text-gray-600 mb-1">Description</label>
            <input
              type="text"
              value={split.description || ""}
              onInput={e => onUpdate("description", (e.target as HTMLInputElement).value)}
              class="w-full px-3 py-2 text-sm border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="Optional"
            />
          </div>
          <div class="flex items-end gap-1 pb-0.5">
            <button onClick={onStopEdit} class="p-2 text-green-600 hover:text-green-800 hover:bg-green-50 rounded">
              <Check size={16} />
            </button>
            <button onClick={onDelete} class="p-2 text-red-500 hover:text-red-700 hover:bg-red-50 rounded">
              <Trash2 size={16} />
            </button>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div
      class="bg-white border border-gray-200 rounded-lg p-3 hover:shadow-sm transition-shadow cursor-pointer"
      onClick={onStartEdit}>
      <div class="flex items-center justify-between">
        <div class="flex items-center gap-4">
          <span class="text-xs font-medium text-gray-400">#{index + 1}</span>
          <span class="font-semibold">{formatCurrency(split.amount)}</span>
          {split.account && <span class="text-sm text-green-700 bg-green-50 px-2 py-0.5 rounded">{split.account}</span>}
          {split.description && <span class="text-sm text-gray-500">{split.description}</span>}
          {!split.account && <span class="text-sm text-gray-400 italic">No account assigned</span>}
        </div>
        <div class="flex gap-1">
          <button
            onClick={e => {
              e.stopPropagation()
              onStartEdit()
            }}
            class="p-1.5 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded">
            <Pencil size={14} />
          </button>
          <button
            onClick={e => {
              e.stopPropagation()
              onDelete()
            }}
            class="p-1.5 text-red-400 hover:text-red-600 hover:bg-red-50 rounded">
            <Trash2 size={14} />
          </button>
        </div>
      </div>
    </div>
  )
}
